import { type ReactNode, type RefObject, useCallback, useRef, useState } from 'react';

export type Column<Row = never> = {
  align?: 'left' | 'center' | 'right';
  key: keyof Row;
  label: ReactNode;
  sortable: boolean;
  sorted?: 'asc' | 'desc';
  value?: (_: unknown) => string | number | Date;
  visible: boolean;
};

export class Table {
  static by<Row>(cols: Column<Row>[]) {
    return cols.find(({ sorted }) => !!sorted);
  }

  static compare<Row>(key: keyof Row, sorted?: 'asc' | 'desc', value?: (_: unknown) => string | number | Date) {
    return value
      ? sorted === 'asc'
        ? (a: Row, b: Row) => (value(a[key]) >= value(b[key]) ? 1 : -1)
        : (a: Row, b: Row) => (value(a[key]) <= value(b[key]) ? 1 : -1)
      : sorted === 'asc'
        ? (a: Row, b: Row) => (a[key] >= b[key] ? 1 : -1)
        : (a: Row, b: Row) => (a[key] <= b[key] ? 1 : -1);
  }

  static sort<Row>(col?: Column<Row>, order?: keyof Row) {
    return col?.sortable
      ? order
        ? (rows: Row[]) => rows.sort(this.compare(order, col.sorted)).sort(this.compare(col.key, col.sorted, col.value))
        : (rows: Row[]) => rows.sort(this.compare(col.key, col.sorted, col.value))
      : (rows: Row[]) => rows;
  }

  static view({ page, total, size }: { page?: number; size: number; total: number }) {
    return {
      get end() {
        return Math.min(this.total, this.page * this.size);
      },
      get length() {
        return this.end - this.start;
      },
      get page() {
        return Math.min(this.pages, Math.max(1, page || 0));
      },
      get pages() {
        return Math.ceil(this.total / this.size) || 0;
      },
      get size() {
        return size || 0;
      },
      get start() {
        return Math.max(0, this.page * this.size - this.size);
      },
      get total() {
        return total || 0;
      },
    };
  }
}

export const useTable = <Row>(props: {
  cols: (view: RefObject<ReturnType<typeof Table.view>>) => Column<Row>[];
  rows: () => Row[];
  view: () => { page?: number; size: number; total: number };
  order?: keyof Row;
}): [
  Column<Row>[],
  RefObject<Row[]>,
  RefObject<ReturnType<typeof Table.view>>,
  RefObject<(col?: Column<Row>) => void>,
] => {
  const view = useRef(Table.view(props.view()));
  const [cols, setCols] = useState(props.cols(view));
  const rows = useRef(
    Table.sort(Table.by(cols), props.order)(props.rows()).splice(view.current.start, view.current.length)
  );
  return [
    cols,
    rows,
    view,
    useRef(
      useCallback(
        col => {
          view.current = Table.view({ page: view.current.page, size: view.current.size, total: props.view().total });
          if ((col?.sortable && (col.sorted = col.sorted === 'desc' ? 'asc' : 'desc')) || (col ??= Table.by(cols))) {
            setCols(
              props
                .cols(view)
                .map(({ label }, index) =>
                  Object.assign(
                    cols[index],
                    { label },
                    cols[index].key === col?.key ? { sorted: col?.sorted } : delete cols[index].sorted
                  )
                )
            );
          }
          rows.current = Table.sort(col, props.order)(props.rows()).splice(view.current.start, view.current.length);
        },
        [props, cols, setCols]
      )
    ),
  ];
};
